/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Package, 
  Calendar, 
  ArrowLeft, 
  X,
  CreditCard,
  AlertCircle,
  ShoppingBag,
  Download,
  Receipt,
  CheckCircle,
  Search,
  MapPin,
  Truck,
  Clock,
  Star,
  RefreshCw,
  Eye,
  MoreVertical,
  Box
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const BACKEND_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5001';

// Helper to get full bill URL
const getFullBillUrl = (billUrl: string | undefined): string => {
  if (!billUrl) return '';
  if (billUrl.startsWith('http')) return billUrl;
  return `${BACKEND_URL}${billUrl.startsWith('/') ? '' : '/'}${billUrl}`;
};

interface OrderItem {
  _id: string;
  name: string | null;
  quantity: number;
  price: number;
  image: string | null;
  productId: string;
  sku?: string;
}

interface ShippingAddress {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface Order {
  _id: string;
  orderNumber: string;
  date: string;
  createdAt: string;
  status: string;
  totalAmount: number;
  items: OrderItem[];
  paymentMethod?: string;
  paymentStatus?: string;
  billFile?: string;
  billUrl?: string;
  billUploadedAt?: string;
  shippingAddress?: ShippingAddress;
  trackingNumber?: string;
  estimatedDelivery?: string;
  discount?: number;
  shipping?: number;
  subtotal?: number;
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

function getStatusConfig(status: string) {
  const configs: Record<string, { 
    color: string; 
    bg: string; 
    border: string;
    text: string;
    icon: React.ReactNode;
    step: number;
  }> = {
    pending: { 
      color: 'text-orange-700', 
      bg: 'bg-orange-50', 
      border: 'border-orange-200',
      text: 'Order Placed',
      icon: <Clock className="w-4 h-4" />,
      step: 1
    },
    processing: { 
      color: 'text-blue-700', 
      bg: 'bg-blue-50', 
      border: 'border-blue-200',
      text: 'Processing',
      icon: <Package className="w-4 h-4" />,
      step: 2
    },
    shipped: { 
      color: 'text-purple-700', 
      bg: 'bg-purple-50', 
      border: 'border-purple-200',
      text: 'Shipped',
      icon: <Truck className="w-4 h-4" />,
      step: 3
    },
    out_for_delivery: { 
      color: 'text-indigo-700', 
      bg: 'bg-indigo-50', 
      border: 'border-indigo-200',
      text: 'Out for Delivery',
      icon: <MapPin className="w-4 h-4" />,
      step: 4
    },
    delivered: { 
      color: 'text-green-700', 
      bg: 'bg-green-50', 
      border: 'border-green-200',
      text: 'Delivered',
      icon: <CheckCircle className="w-4 h-4" />,
      step: 5
    },
    cancelled: { 
      color: 'text-red-700', 
      bg: 'bg-red-50', 
      border: 'border-red-200',
      text: 'Cancelled',
      icon: <X className="w-4 h-4" />,
      step: 0
    },
  };
  
  return configs[status.toLowerCase()] || { 
    color: 'text-gray-700', 
    bg: 'bg-gray-50', 
    border: 'border-gray-200',
    text: status,
    icon: <Box className="w-4 h-4" />,
    step: 0
  };
}

function StatusBadge({ status }: { status: string }) {
  const config = getStatusConfig(status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${config.bg} ${config.color} ${config.border} border`}>
      {config.icon}
      {config.text}
    </span>
  );
}

function OrderTimeline({ status }: { status: string }) {
  const steps = [
    { key: 'pending', label: 'Order Placed', icon: Clock },
    { key: 'processing', label: 'Processing', icon: Package },
    { key: 'shipped', label: 'Shipped', icon: Truck },
    { key: 'out_for_delivery', label: 'Out for Delivery', icon: MapPin },
    { key: 'delivered', label: 'Delivered', icon: CheckCircle },
  ];
  
  const currentStep = getStatusConfig(status).step;
  
  return (
    <div className="flex items-center justify-between mt-4 px-2">
      {steps.map((step, index) => {
        const Icon = step.icon;
        const isCompleted = index < currentStep;
        const isCurrent = index === currentStep - 1;
        const isCancelled = status.toLowerCase() === 'cancelled';
        
        return (
          <div key={step.key} className="flex flex-col items-center flex-1 relative">
            {index < steps.length - 1 && (
              <div 
                className={`absolute top-3 left-1/2 w-full h-0.5 -translate-y-1/2 ${
                  isCompleted && !isCancelled ? 'bg-green-500' : 'bg-gray-200'
                }`}
              />
            )}
            <div 
              className={`w-6 h-6 rounded-full flex items-center justify-center z-10 ${
                isCancelled ? 'bg-red-100 text-red-600' :
                isCompleted ? 'bg-green-500 text-white' :
                isCurrent ? 'bg-blue-500 text-white ring-4 ring-blue-100' :
                'bg-gray-200 text-gray-400'
              }`}
            >
              <Icon className="w-3 h-3" />
            </div>
            <span className={`text-xs mt-1.5 font-medium ${
              isCancelled ? 'text-red-600' :
              isCompleted || isCurrent ? 'text-gray-900' : 'text-gray-400'
            }`}>
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function OrderCard({ order, onCancel, onViewDetails }: { order: Order; onCancel: (id: string) => void; onViewDetails: (order: Order) => void }) {
  const canCancel = ['pending', 'processing'].includes(order.status.toLowerCase());
  const hasBill = !!order.billFile && !!order.billUrl;
  const isDelivered = order.status.toLowerCase() === 'delivered';
  const firstItem = order.items[0];
  const remainingCount = order.items.length - 1;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6 hover:shadow-md transition-shadow">
      {/* Order Header */}
      <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-b border-gray-100">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-white p-2.5 rounded-xl shadow-sm border border-gray-100">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-bold text-gray-900 text-lg">Order #{order.orderNumber}</span>
                <StatusBadge status={order.status} />
                {hasBill && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-green-100 text-green-700">
                    <Receipt className="w-3 h-3" />
                    Invoice Ready
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4 mt-1.5 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {order.date}
                </span>
                <span className="w-1 h-1 rounded-full bg-gray-300" />
                <span className="flex items-center gap-1">
                  <CreditCard className="w-3.5 h-3.5" />
                  {order.paymentMethod?.toUpperCase() || 'COD'}
                </span>
                {order.trackingNumber && (
                  <>
                    <span className="w-1 h-1 rounded-full bg-gray-300" />
                    <span className="flex items-center gap-1 text-blue-600">
                      <Truck className="w-3.5 h-3.5" />
                      Track: {order.trackingNumber}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(order.totalAmount)}</p>
            <p className="text-sm text-gray-500">{order.items.length} item{order.items.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        
        {/* Timeline */}
        {!['cancelled'].includes(order.status.toLowerCase()) && (
          <OrderTimeline status={order.status} />
        )}
      </div>

      {/* Order Items Preview */}
      <div className="px-6 py-4">
        <div className="flex items-start gap-4">
          {/* Product Image */}
          <div className="w-20 h-20 bg-gray-50 rounded-xl overflow-hidden flex-shrink-0 border border-gray-100">
            {firstItem?.image ? (
              <img 
                src={firstItem.image} 
                alt={firstItem.name || 'Product'}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/placeholder-product.jpg';
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300">
                <Package className="w-8 h-8" />
              </div>
            )}
          </div>
          
          {/* Product Info */}
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-900 mb-1">
              {firstItem?.name || <span className="text-gray-400 italic">Product Removed</span>}
            </h4>
            {firstItem?.sku && (
              <p className="text-xs text-gray-500 mb-2">SKU: {firstItem.sku}</p>
            )}
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="font-semibold text-gray-900">{formatCurrency(firstItem?.price || 0)}</span>
              <span className="text-gray-400">×</span>
              <span className="text-gray-600">{firstItem?.quantity || 0}</span>
              {remainingCount > 0 && (
                <span className="text-blue-600 font-medium">+{remainingCount} more</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <button 
              onClick={() => onViewDetails(order)}
              className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-xl hover:border-blue-300 hover:text-blue-600 text-sm font-medium transition-all"
            >
              <Eye className="w-4 h-4" />
              View Details
            </button>
            
            {hasBill && (
              <a
                href={getFullBillUrl(order.billUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 border border-green-200 rounded-xl hover:bg-green-100 text-sm font-medium transition-all"
              >
                <Download className="w-4 h-4" />
                Download Invoice
              </a>
            )}
            
            {isDelivered && (
              <button className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-xl hover:border-yellow-300 hover:text-yellow-600 text-sm font-medium transition-all">
                <Star className="w-4 h-4" />
                Rate Order
              </button>
            )}
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            {canCancel && (
              <button
                onClick={() => onCancel(order._id)}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-xl hover:bg-red-100 text-sm font-medium transition-all"
              >
                <X className="w-4 h-4" />
                Cancel Order
              </button>
            )}
            
            <button className="p-2 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition-all">
              <MoreVertical className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function OrderDetailsModal({ order, onClose, onCancel }: { order: Order; onClose: () => void; onCancel: (id: string) => void }) {
  if (!order) return null;
  
  const canCancel = ['pending', 'processing'].includes(order.status.toLowerCase());
  const hasBill = !!order.billFile && !!order.billUrl;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Order #{order.orderNumber}</h2>
            <p className="text-sm text-gray-500 mt-1">Placed on {order.date}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Status & Timeline */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <StatusBadge status={order.status} />
              {hasBill && (
                <a
                  href={getFullBillUrl(order.billUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                >
                  <Download className="w-4 h-4" />
                  Download Invoice
                </a>
              )}
            </div>
            {!['cancelled'].includes(order.status.toLowerCase()) && (
              <OrderTimeline status={order.status} />
            )}
          </div>

          {/* Items */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Order Items</h3>
            <div className="space-y-3">
              {order.items.map((item) => (
                <div key={item._id} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-16 h-16 bg-white rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                    {item.image ? (
                      <img src={item.image} alt={item.name || ''} className="w-full h-full object-cover" />
                    ) : (
                      <Package className="w-full h-full p-4 text-gray-300" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{item.name || 'Product Removed'}</h4>
                    {item.sku && <p className="text-sm text-gray-500">SKU: {item.sku}</p>}
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className="font-semibold">{formatCurrency(item.price)}</span>
                      <span className="text-gray-400">×</span>
                      <span>{item.quantity}</span>
                      <span className="text-gray-400">=</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(item.price * item.quantity)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Price Breakdown */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Price Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span>{formatCurrency(order.subtotal || order.totalAmount)}</span>
              </div>
              {(order.discount || 0) > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount</span>
                  <span>-{formatCurrency(order.discount || 0)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Shipping</span>
                <span>{(order.shipping || 0) > 0 ? formatCurrency(order.shipping || 0) : 'Free'}</span>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between font-semibold text-lg">
                  <span>Total</span>
                  <span>{formatCurrency(order.totalAmount)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          {order.shippingAddress && (
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Shipping Address
              </h3>
              <p className="text-sm text-gray-600">
                {order.shippingAddress.street}<br />
                {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}<br />
                {order.shippingAddress.country}
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-6 py-2 border border-gray-200 rounded-xl hover:bg-gray-50">
            Close
          </button>
          {canCancel && (
            <button
              onClick={() => {
                onCancel(order._id);
                onClose();
              }}
              className="px-6 py-2 bg-red-50 text-red-600 border border-red-200 rounded-xl hover:bg-red-100"
            >
              Cancel Order
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatsCard({ icon: Icon, label, value, color }: { icon: any, label: string, value: number, color: string }) {
  const colors: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
  };
  
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );
}


function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchOrders();
    
    // Auto-refresh when page becomes visible (e.g., after returning from admin panel)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchOrders();
      }
    };
    
    // Also refresh on window focus
    const handleFocus = () => {
      fetchOrders();
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    // Poll for updates every 30 seconds
    const pollInterval = setInterval(fetchOrders, 30000);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      clearInterval(pollInterval);
    };
  }, []);

  async function fetchOrders() {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('token');
      if (!token) {
        // ProtectedRoute handles this, but keep as fallback
        console.error('No token found');
        setRefreshing(false);
        return;
      }
      
      const response = await fetch(`${API_URL}/orders/my-orders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const data = await response.json();
      
      console.log('Orders API Response:', data);
      if (data.success && data.data) {
        const transformedOrders = data.data.map((order: any) => ({
          _id: order._id,
          orderNumber: order.orderNumber,
          createdAt: order.createdAt,
          date: formatDate(order.createdAt),
          status: order.status,
          totalAmount: order.totals?.grandTotal || order.total || 0,
          subtotal: order.totals?.subtotal || 0,
          discount: order.totals?.discount || 0,
          shipping: order.totals?.shipping || 0,
          paymentMethod: order.payment?.method || 'COD',
          paymentStatus: order.payment?.status || 'pending',
          billFile: order.billFile,
          billUrl: order.billUrl,
          billUploadedAt: order.billUploadedAt,
          trackingNumber: order.shipping?.trackingNumber || null,
          estimatedDelivery: order.shipping?.estimatedDelivery || null,
          shippingAddress: order.shippingAddress || order.shipping?.address || null,
          items: order.items?.map((item: any, index: number) => {
            const productName = item.product?.name || item.name || null;
            const productImage = item.product?.images?.[0]?.url || item.product?.images?.[0] || item.image || null;
            let imageUrl = productImage;
            if (productImage && !productImage.startsWith('http') && !productImage.startsWith('data:')) {
              imageUrl = `${BACKEND_URL}${productImage.startsWith('/') ? '' : '/'}${productImage}`;
            }
            
            return {
              _id: item._id || index.toString(),
              name: productName,
              quantity: item.quantity,
              price: item.price?.selling || item.price || 0,
              image: imageUrl,
              productId: item.product?._id || item.product || '',
              sku: item.product?.sku || item.sku || null
            };
          }) || []
        }));
        
        setOrders(transformedOrders);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  async function handleCancelOrder(orderId: string) {
    if (!window.confirm('Are you sure you want to cancel this order?\n\nThis action cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/orders/${orderId}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setOrders(orders.map(order => 
          order._id === orderId ? { ...order, status: 'cancelled' } : order
        ));
        alert('Order cancelled successfully');
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to cancel order');
      }
    } catch (error) {
      console.error('Cancel order error:', error);
      alert('Failed to cancel order');
    }
  }

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      // Filter by status
      if (filter === 'active') {
        if (!['pending', 'processing', 'shipped', 'out_for_delivery'].includes(order.status.toLowerCase())) return false;
      } else if (filter === 'delivered') {
        if (order.status.toLowerCase() !== 'delivered') return false;
      } else if (filter === 'cancelled') {
        if (order.status.toLowerCase() !== 'cancelled') return false;
      } else if (filter === 'has_bill') {
        if (!order.billFile) return false;
      }
      
      // Search
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesOrderNumber = order.orderNumber.toLowerCase().includes(term);
        const matchesProduct = order.items.some(item => 
          item.name?.toLowerCase().includes(term)
        );
        if (!matchesOrderNumber && !matchesProduct) return false;
      }
      
      return true;
    });
  }, [orders, filter, searchTerm]);

  const stats = useMemo(() => ({
    total: orders.length,
    active: orders.filter(o => ['pending', 'processing', 'shipped', 'out_for_delivery'].includes(o.status.toLowerCase())).length,
    delivered: orders.filter(o => o.status.toLowerCase() === 'delivered').length,
    cancelled: orders.filter(o => o.status.toLowerCase() === 'cancelled').length,
    withBills: orders.filter(o => o.billFile).length
  }), [orders]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50/50">
        <div className="bg-white shadow-sm sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4 h-16">
              <Link to="/" className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </Link>
              <h1 className="text-xl font-bold text-gray-900">My Orders</h1>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-6" />
                <div className="flex gap-4">
                  <div className="h-20 w-20 bg-gray-200 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Empty State
  if (orders.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50/50">
        <div className="bg-white shadow-sm sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4 h-16">
              <Link to="/" className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </Link>
              <h1 className="text-xl font-bold text-gray-900">My Orders</h1>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="max-w-md mx-auto text-center">
            <div className="w-32 h-32 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner">
              <ShoppingBag className="w-16 h-16 text-blue-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">No Orders Yet</h2>
            <p className="text-gray-500 mb-8">Start shopping and your orders will appear here.</p>
            <Link to="/products" className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all">
              <ShoppingBag className="w-5 h-5" />
              Start Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </Link>
              <h1 className="text-xl font-bold text-gray-900">My Orders</h1>
            </div>
            <button 
              onClick={fetchOrders}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="text-sm font-medium">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatsCard icon={ShoppingBag} label="Total Orders" value={stats.total} color="blue" />
          <StatsCard icon={Clock} label="Active" value={stats.active} color="orange" />
          <StatsCard icon={CheckCircle} label="Delivered" value={stats.delivered} color="green" />
          <StatsCard icon={Receipt} label="With Bills" value={stats.withBills} color="purple" />
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by order number or product..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Filter Pills */}
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: 'All', count: stats.total },
                { key: 'active', label: 'Active', count: stats.active },
                { key: 'delivered', label: 'Delivered', count: stats.delivered },
                { key: 'cancelled', label: 'Cancelled', count: stats.cancelled },
                { key: 'has_bill', label: 'Has Bill', count: stats.withBills },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setFilter(tab.key)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    filter === tab.key 
                      ? 'bg-blue-600 text-white shadow-md shadow-blue-200' 
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {tab.label}
                  <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                    filter === tab.key ? 'bg-white/20' : 'bg-gray-200'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-gray-600">
            Showing <span className="font-semibold text-gray-900">{filteredOrders.length}</span> of <span className="font-semibold text-gray-900">{orders.length}</span> orders
          </p>
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm('')}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Clear search
            </button>
          )}
        </div>

        {/* Orders List */}
        <div className="space-y-6">
          {filteredOrders.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No orders found</h3>
              <p className="text-gray-500">
                {searchTerm ? 'Try adjusting your search or filters.' : 'No orders match the selected filter.'}
              </p>
            </div>
          ) : (
            filteredOrders.map((order) => (
              <OrderCard 
                key={order._id} 
                order={order} 
                onCancel={handleCancelOrder}
                onViewDetails={(o) => {
                  setSelectedOrder(o);
                  setShowDetailsModal(true);
                }}
              />
            ))
          )}
        </div>
      </div>

      {/* Order Details Modal */}
      {showDetailsModal && selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedOrder(null);
          }}
          onCancel={handleCancelOrder}
        />
      )}
    </div>
  );
}

export default OrdersPage;
